import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { InvitationsTable } from './InvitationsTable';
import { InviteMemberModal } from './InviteMemberModal';
import { useToast } from '../../contexts/ToastContext';
import { toUserErrorMessage } from '../../lib/apiError';
import { useInvitations } from '../../services/api/hooks/useInvitations';
import { useResendInvitation } from '../../services/api/hooks/useResendInvitation';
import type { ExtranetInvitationOutput } from '../../types/extranet-api.types';
import { OffsetPagination } from '../common/OffsetPagination';

const DEFAULT_LIMIT = 20;

export const InvitationsTab = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0));
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);

  const query = useInvitations({ offset, limit });
  const resend = useResendInvitation();

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });

  const handleResend = (invitation: ExtranetInvitationOutput) => {
    setResendingId(invitation.invitationId);
    resend.mutate(invitation.invitationId, {
      onSuccess: () => toast.success('Invitation renvoyée', `Un nouvel email a été envoyé à ${invitation.email}.`),
      onError: err => {
        const { title, description } = toUserErrorMessage(err, 'Échec du renvoi');
        toast.error(title, description);
      },
      onSettled: () => setResendingId(null),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-zinc-500">
          Un membre invité reçoit un email pour choisir son mot de passe. Ses rôles sont fixés à l&apos;invitation.
        </p>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="h-8 px-3 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors duration-100 whitespace-nowrap"
        >
          + Inviter un membre
        </button>
      </div>

      <InvitationsTable
        items={query.data ?? []}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        isError={query.isError}
        error={query.error}
        onResend={handleResend}
        resendingId={resendingId}
      />

      <OffsetPagination
        offset={offset}
        limit={limit}
        pageSize={query.data?.length ?? 0}
        onOffsetChange={next => updateParams(p => p.set('offset', String(next)))}
        onLimitChange={next =>
          updateParams(p => {
            p.set('limit', String(next));
            p.set('offset', '0');
          })
        }
      />

      <InviteMemberModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
};
